import { useEffect, useState } from 'react';
import {
  Combobox, useListCollection, Portal,
  ProgressCircle,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import type { UserType } from '@customTypes/user';

type Props = {
  errors: FormErrors | null;
  users: UserType[];
  onSearch: (value: string) => Promise<void>;
  onChange: (user: UserType | null) => void;
}

const UserSelect = ({
  errors, users, onSearch, onChange,
}: Props) => {
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);

  const { collection, set } = useListCollection({
    initialItems: users,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
  })

  const updateSearch = (value:string) => {
    if (value !== search) {
      setSearching(true);
      setSearch(value);
      if (searchTimer) clearTimeout(searchTimer);
      setSearchTimer(setTimeout(async () => {
        await onSearch(value);
        setSearching(false);
      }, 1000));
    }
  };

  const onValueChange = (value: UserType | null) => {
    onChange(value);
    setSearch('');
  };

  useEffect(() => {
    if (users) {
      set(users);
    }
  }, [users, set]);

  return (
    <Combobox.Root
      collection={collection}
      onValueChange={({ items }) => onValueChange(items[0] || null)}
      invalid={!!errors?.user}
      variant="subtle"
      value={[]}
    >
      <Combobox.Label>Usuário</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder="Pesquisar..." value={search} onChange={(e) => updateSearch(e.target.value)} />
        <Combobox.IndicatorGroup>
          {searching && (
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )}
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content zIndex="popover">
            <Combobox.Empty>Nenhum item encontrado</Combobox.Empty>
            {collection.items.map((item) => (
              <Combobox.Item item={item} key={item.id}>
                {item.name}
                <Combobox.ItemIndicator />
              </Combobox.Item>
            ))}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

export default UserSelect;
